import { Component, DestroyRef, inject, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserProfile } from 'src/app/models/profile.model';
import { StreApperInfo } from 'src/app/models/streapper-info.model';
import { AuthService } from 'src/app/services/auth.service';
import { StreappEventsService } from 'src/app/services/streapp-events.service';
import { DeleteAccountDialogComponent } from '../_components/delete-account-dialog/delete-account-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ContactDialogComponent } from 'src/app/components/contact-dialog/contact-dialog.component';
import { ShareDialogComponent } from 'src/app/components/share-dialog/share-dialog.component';
import { isPlatformServer } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'profile',
    templateUrl: './profile.component.html',
    styleUrl: './profile.component.scss',
    standalone: false
})

export class ProfileComponent implements OnInit{
  private readonly destroyRef = inject(DestroyRef);

  amountOfStreappsPosted: number = 0;
  monthlySebbiePoints: number = 0;
  totalSebbiePoints: number = 0;

  loading: boolean = true;
  error: boolean = false;
  errorText: string;

  userProfile: UserProfile;
  userProfile$: Observable<UserProfile> = this.authService.userProfile$;

  profile: StreApperInfo;
  subscriptions: string[] = [];
  userId: string;
  
  private _isSubscribed = new BehaviorSubject<boolean>(false);
  public isSubscribed$: Observable<boolean> = this._isSubscribed.asObservable();

  private _isStreapper = new BehaviorSubject<boolean>(false);
  public isStreapper$: Observable<boolean> = this._isStreapper.asObservable();
  
  isSupporter: boolean = false;

  constructor(
    private readonly streappService: StreappEventsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private dialog: MatDialog,
    @Inject(PLATFORM_ID) private _platformId: Object
  ) {}

  ngOnInit(): void {
    if (isPlatformServer(this._platformId)) {
      return;
    }

    if (this.isLoggedIn()) {
      this.userProfile$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data =>
          this.userProfile = data
        )

      //user info (username, firstname etc.)
      this.authService
        .getClaimData(true)
        .then((data) => {
          // do nothing with data here, as observable is listened to
          // wil change later along with to be void return;
        })
        .catch((error) => {
          this.errorText = error;
        });

      //user profile content (banner, streapps etc.)
      this.route.queryParams
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((params) => {
          this.userId = this.authService.userId;
          this.getStreapperInfoId(this.userId);
          this.isSupporter = this.authService.isSupporter;
          // observer for children components
          this._isSubscribed.next(this.authService.isSubscribed);
          this._isStreapper.next(this.authService.isStreapper);
          this.subscriptions = [...this.authService.subRoles, ...this.authService.extrasSubRoles]
        });
    }
  }

  public navigateToSebbiePage(): void {
    if(!this._isSubscribed.getValue()) return;

    this.router.navigate(['/ranglijst']);
  }

  public navigateToTasksPage(): void {
    if(!this._isStreapper.getValue()) return;

    this.router.navigate(['/taken']);
  }

  async getStreapperInfoId(id: string): Promise<void> {
    try {
      this.profile = await this.streappService.getStreapperInfoId(id);
      if (this.profile.banner) {
        this.profile.banner = this.profile.banner + '?' + new Date().getTime();
      }
    } catch (error) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  contactAdmin(): void {
    this.dialog.open<ContactDialogComponent>(ContactDialogComponent, {
      autoFocus: false, // To prevent select state of close button in header
    });
  }

  logout(): void {
    this.authService.signout();
  }

  share(): void {
    this.dialog.open<ShareDialogComponent>(ShareDialogComponent, {
      autoFocus: false, // To prevent select state of close button in header
      data: {
        url: `https://streapp.com/${this._isStreapper.getValue() ? 'streappers' : 'users'}/detail?id=${this.profile.id}`,
        title: this.profile.name,
        facebook: true,
        x: true,
      },
    });
  }

  deleteAccount(): void {
    this.dialog.open<DeleteAccountDialogComponent>(
      DeleteAccountDialogComponent,
      {
        autoFocus: false, // To prevent select state of close button in header
      }
    );
  }

  isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }
}
